// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { CostFilter } from '@/costs/api/get-costs';
import { useCostAggregate } from '@/costs/hooks/use-cost-aggregate';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { Box, Grid, LinearProgress, Skeleton } from '@mui/material';
import { Fragment } from 'react';

/**
 * Props for the CostsSummary component
 */
interface Props {
  /**
   * Filter criteria to determine which costs to include in the summary.
   */
  filter?: Omit<CostFilter, 'pageSize' | 'pageNumber' | 'sort'>;
}

/**
 * Skeleton component for loading state
 */
function CostsSummarySkeleton() {
  return (
    <Box sx={{ marginBottom: 2 }}>
      <Grid container spacing={3}>
        {[1, 2, 3].map((index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <SummaryCard>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="80%" height={48} />
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/**
 * A component that displays cost summary aggregated across filtered cost entries
 */
export function CostsSummary(props: Props) {
  const { aggregateData, isLoading, isFetching } = useCostAggregate(
    props.filter
  );

  if (isLoading) {
    return <CostsSummarySkeleton />;
  }

  const totalEntries = aggregateData.totalEntries;

  // Empty state: no entries at all.
  if (totalEntries === 0) {
    return (
      <Box sx={{ marginBottom: 1 }}>
        {isFetching && <LinearProgress />}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard>
              <PrimaryMetric label="Total costs reported" value="—" />
            </SummaryCard>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Costs with no currency carry no money bucket, so they live outside byCurrency;
  // the remainder of the grand total is their entry count.
  const reportedEntries = aggregateData.byCurrency.reduce(
    (sum, bucket) => sum + bucket.totalEntries,
    0
  );
  const noCurrencyEntries = totalEntries - reportedEntries;
  const hasNoCurrency = noCurrencyEntries > 0;

  // Only qualify the count label with a currency when more than one group is
  // shown; a single group needs no disambiguation.
  const groupCount = aggregateData.byCurrency.length + (hasNoCurrency ? 1 : 0);
  const countLabelSuffix = (suffix: string) =>
    groupCount > 1 ? ` (${suffix})` : '';

  return (
    <Box sx={{ marginBottom: 1 }}>
      {isFetching && <LinearProgress />}
      <Grid container spacing={3}>
        {aggregateData.byCurrency.map((bucket) => (
          <Fragment key={bucket.isoCode}>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total costs reported${countLabelSuffix(bucket.isoCode)}`}
                  value={bucket.totalEntries.toLocaleString()}
                />
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total value reported (${bucket.isoCode})`}
                  value={formatAmountWithCurrency(
                    bucket.totalAmount,
                    bucket.isoCode
                  )}
                />
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Average cost reported (${bucket.isoCode})`}
                  value={formatAmountWithCurrency(
                    bucket.averageAmount,
                    bucket.isoCode
                  )}
                />
              </SummaryCard>
            </Grid>
          </Fragment>
        ))}
        {hasNoCurrency && (
          <Grid item xs={12} sm={6} md={4} key="no-currency">
            <SummaryCard>
              <PrimaryMetric
                label={`Total costs reported${countLabelSuffix('No currency')}`}
                value={noCurrencyEntries.toLocaleString()}
              />
            </SummaryCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
